import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { IAcademySsoLink } from '../../../shared/interfaces/academy-integration.interface';
import { AppConfigService } from './app-config.service';
import { ErrorMessageService } from './error-message.service';
import { LoadErrorHandler } from './load-error-handler';
import { TenantService } from './tenant.service';
import { UserAclService } from './user-acl.service';
import { AclSecurityAdapter } from './acl.service';
import { RequiredSecurities } from '../../../shared/constants/required-securities';

@Injectable({ providedIn: 'root' })
export class AcademyService implements OnDestroy {
  private ssoLinkSubscription: Subscription;

  constructor(
    private appConfigService: AppConfigService,
    private errorMessageService: ErrorMessageService,
    private loadErrorHandler: LoadErrorHandler,
    private tenantService: TenantService,
    private userAclService: UserAclService,
  ) {}

  public isAcademyAvailable$(): Observable<boolean> {
    if (!this.appConfigService.isTenantEnabled()) {
      return of(false);
    }

    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter.useFunctionalActions(RequiredSecurities.Academy_Read.functionalActions).check(),
      ),
      switchMap((userHasAccess: boolean): Observable<boolean> => {
        return userHasAccess ? this.tenantService.isAcademyAvailable() : of(false);
      }),
      catchError(() => of(false)),
    );
  }

  private onGenerateSSOLinkSuccess(link: IAcademySsoLink): void {
    this.unsubscribeSsoLinkSubscription();
    window.open(link.url, '_blank');
  }

  private onGenerateSSOLinkError(error: HttpErrorResponse): void {
    this.unsubscribeSsoLinkSubscription();
    const messageKey: string = this.errorMessageService.getAppErrorResponse(error);
    this.loadErrorHandler.handleError(messageKey, error, 'AcademyService');
  }

  private unsubscribeSsoLinkSubscription(): void {
    if (this.ssoLinkSubscription) {
      this.ssoLinkSubscription.unsubscribe();
      this.ssoLinkSubscription = null;
    }
  }

  public subscribeForSsoLink(): void {
    this.unsubscribeSsoLinkSubscription();
    this.ssoLinkSubscription = this.tenantService.generateAcademySSOLink().subscribe({
      next: this.onGenerateSSOLinkSuccess.bind(this),
      error: this.onGenerateSSOLinkError.bind(this),
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribeSsoLinkSubscription();
  }
}
